import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import {
  emptySkuRuleForm,
  fromSkuRule,
  toSkuRuleCreate,
  toSkuRuleUpdate,
} from '../api/sku-rule-mappers';
import { useCreateSkuRule, useUpdateSkuRule } from '../hooks/use-sku-rule-mutations';
import { useSkuRule } from '../hooks/use-sku-rules';

import { SkuRuleApplySection } from './sku-rule-apply-dialog';
import { SkuRuleDangerZone } from './sku-rule-danger-zone';
import { SkuRuleForm } from './sku-rule-form';

const LIST_PATH = '/org/catalog-setup/sku-rules' as const;

/** Sits under the catalog setup header and tabs, so it is a second-level heading. */
function EditorHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  const { t } = useTranslation('skuRules');
  return (
    <div className="flex flex-col gap-2">
      <Button asChild variant="ghost" size="sm" className="-ml-2 self-start">
        <Link to={LIST_PATH}>
          <ArrowLeft aria-hidden />
          {t('backToList')}
        </Link>
      </Button>
      <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold">{title}</h2>
      {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
    </div>
  );
}

export function SkuRuleCreate() {
  const { t } = useTranslation('skuRules');
  const navigate = useNavigate();
  const create = useCreateSkuRule();

  return (
    <div className="flex flex-col gap-4">
      <EditorHeader title={t('new')} description={t('newDescription')} />
      <SkuRuleForm
        rule={null}
        defaultValues={emptySkuRuleForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const row = await create.mutateAsync(toSkuRuleCreate(values));
          await navigate({
            to: '/org/catalog-setup/sku-rules/$ruleId',
            params: { ruleId: row.id },
            replace: true,
          });
        }}
      />
    </div>
  );
}

export function SkuRuleEdit({ ruleId }: { ruleId: string }) {
  const { t } = useTranslation('skuRules');
  const navigate = useNavigate();
  // Prefetched by the route loader, so this normally resolves from cache.
  const query = useSkuRule(ruleId);
  const update = useUpdateSkuRule(ruleId);
  const rule = query.data;

  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!rule) return <Skeleton className="h-64" />;

  return (
    <div className="flex flex-col gap-4">
      <EditorHeader
        title={
          <>
            {rule.name}
            {rule.isDefault ? (
              <Badge variant="secondary" className="font-normal">
                {t('list.default')}
              </Badge>
            ) : null}
          </>
        }
      />
      <SkuRuleForm
        key={rule.updatedAt}
        rule={rule}
        defaultValues={fromSkuRule(rule)}
        isSubmitting={update.isPending}
        onSubmit={(values) => update.mutateAsync(toSkuRuleUpdate(values, rule))}
        aside={
          <Can permission="attributes.write">
            <SkuRuleApplySection rule={rule} />
            <SkuRuleDangerZone
              rule={rule}
              onDeleted={() => {
                // The rule is gone; a dirty form must not keep us on its page.
                releaseSaveBar();
                void navigate({ to: LIST_PATH });
              }}
            />
          </Can>
        }
      />
    </div>
  );
}
